<!-- design-lint-disable no-client-validation (the only browser check is that a video file was picked before its upload; the server checks everything else) -->
<script>
  // Seeds a quest's pinned Example (mode="example") or an extra curator post, unrated
  // unless a level is picked (mode="curator_post"). Any page can open it with just a quest id: the
  // quest + poster options are the `create_example` shared prop
  // (AdminSite::CreateExampleProp), loaded by a partial reload. Creating is an
  // Inertia visit that redirects back with a flash; `reload` names the host page's
  // props that visit refreshes. Only a video's S3 PUT runs outside Inertia; its prepare
  // answers with the `prepared_upload` prop (lib/presigned_upload.js).
  import Modal from '../modal.svelte'
  import ImageUploadDrawer from '../image_upload_drawer.svelte'
  import ClickableImage from '../lightbox/clickable_image.svelte'
  import VideoThumbnailPicker from './video_thumbnail_picker.svelte'
  import UserAvatarUploadModal from '../user_avatar_upload_modal.svelte'
  import { page, router } from '@inertiajs/svelte'
  import { Button, Combobox } from '../ui'
  import { requestPreparedUpload, uploadPrepared } from '../../lib/presigned_upload.js'
  import { VIDEO_MIME_TYPE, MAX_VIDEO_DURATION_LABEL_SECONDS, validateVideoFile } from '../../lib/video_file.js'

  let { open = $bindable(false), questId = null, mode = 'example', reload = [], oncreated, onclose } = $props()

  const isCuratorPost = $derived(mode === 'curator_post')
  const submitLabel = $derived(isCuratorPost ? 'Create curator post' : 'Create Example')

  // The prop answers for whichever quest was last requested; ignore it until it's this one.
  const loaded = $derived($page.props.create_example?.quest?.id === questId ? $page.props.create_example : null)
  const quest = $derived(loaded?.quest ?? null)
  const posters = $derived(loaded?.posters ?? { brand_account: null, curators: [] })
  let loading = $state(false)
  let loadFailed = $state(false)

  let postedAsUserId = $state('')
  let placeId = $state('')
  // '' = untouched, so the server default (the quest's active start) applies.
  let postedAt = $state('')
  // 'none' = unrated; the server's option list (Unrated first) drives the picker.
  let curationLevel = $state('none')
  let mainImage = $state(null)
  let selfieImage = $state(null)
  let mainUploadOpen = $state(false)
  let selfieUploadOpen = $state(false)
  let videoFile = $state(null)
  let thumbnailBlob = $state(null)
  let videoUrl = $state(null)
  let checkingVideo = $state(false)
  let dragging = $state(false)
  let submitting = $state(false)
  let uploadProgress = $state(null)
  let error = $state(null)

  const isVideo = $derived(quest?.capture_mode === 'video')
  const isDualPhoto = $derived(quest?.capture_mode === 'dual_photo')

  const posterList = $derived([posters.brand_account, ...posters.curators].filter(Boolean))
  const posterOptions = $derived(posterList.map((user) => ({ value: user.id, label: user.label })))
  const selectedPoster = $derived(posterList.find((user) => user.id === postedAsUserId))

  // Curators need a picture on day one; setting one is a User#update_icon? action.
  const canEditAvatar = $derived(!!loaded?.can_edit_avatar)
  let avatarModalOpen = $state(false)

  // Refreshes the prop (the poster list) only, so the rest of the in-progress form survives.
  const reloadPrompt = (id, { onSuccess, onError } = {}) =>
    router.reload({ only: ['create_example'], data: { create_example_quest_id: id }, preserveUrl: true, onSuccess, onError })

  const placeOptions = $derived(quest?.place_options ?? [])
  const curationLevels = $derived(loaded?.curation_levels ?? [])
  const ratingDefinitions = $derived(
    curationLevels
      .filter((level) => level.definition)
      .map((level) => `${level.label}: ${level.definition}`)
      .join(' · ')
  )
  const ratingHelp = $derived(ratingDefinitions ? `${ratingDefinitions}.` : '')

  const reset = () => {
    postedAsUserId = ''
    placeId = ''
    postedAt = ''
    curationLevel = 'none'
    mainImage = null
    selfieImage = null
    videoFile = null
    thumbnailBlob = null
    uploadProgress = null
    error = null
  }

  const load = (id) => {
    reset()
    loading = true
    loadFailed = false
    reloadPrompt(id, {
      onSuccess: (response) => {
        if (id !== questId) return
        const prompt = response.props.create_example
        loading = false
        if (prompt?.quest?.id !== id) {
          loadFailed = true
          return
        }
        postedAsUserId = prompt.posters.brand_account?.id ?? ''
      },
      onError: () => {
        loading = false
        loadFailed = true
      }
    })
  }

  let loadedKey = null
  $effect(() => {
    const key = open && questId ? `${questId}:${mode}` : null
    if (key === loadedKey) return
    loadedKey = key
    // The modal stays mounted while closed, so reset on close too: otherwise the
    // picked video keeps playing and its file stays referenced.
    if (key) load(questId)
    else reset()
  })

  $effect(() => {
    if (!videoFile) return
    const url = URL.createObjectURL(videoFile)
    videoUrl = url
    return () => {
      videoUrl = null
      URL.revokeObjectURL(url)
    }
  })

  const selectVideo = async (file) => {
    if (!file) return
    error = null
    checkingVideo = true
    try {
      // Proves the clip decodes before accepting it; the picker below replaces
      // this default first frame with whatever the admin scrubs to.
      const { thumbnail } = await validateVideoFile(file)
      videoFile = file
      thumbnailBlob = thumbnail
    } catch (e) {
      error = { title: e.title || 'Invalid video', text: e.message }
    } finally {
      checkingVideo = false
    }
  }

  const onVideoPicked = (event) => {
    selectVideo(event.target.files?.[0])
    event.target.value = ''
  }

  const onVideoDropped = (event) => {
    event.preventDefault()
    dragging = false
    if (!submitting) selectVideo(event.dataTransfer?.files?.[0])
  }

  const removeVideo = () => {
    videoFile = null
    thumbnailBlob = null
  }

  // Prepare carries the Posted at the create will send, so the server refuses a
  // time past the quest's end before the upload, not after it.
  const uploadVideoMedia = () =>
    uploadPrepared(
      () =>
        requestPreparedUpload('/quest-examples/prepare_media', {
          quest_id: quest.id,
          user_id: postedAsUserId,
          ...(postedAt ? { posted_at: postedAt } : {})
        }),
      { video: videoFile, thumbnail: thumbnailBlob },
      { onProgress: (fraction) => (uploadProgress = fraction) }
    )

  // Only the file has to be checked here, before its upload; everything else (images,
  // poster, posted time, rating) the server validates and answers as errors.
  const validate = () => (isVideo && (!videoFile || !thumbnailBlob) ? { title: 'A video is required.' } : null)

  const finish = () => {
    submitting = false
    uploadProgress = null
  }

  const failed = (text) => {
    error = { title: 'Create failed', text }
    finish()
  }

  const submit = async () => {
    error = validate()
    if (error) return

    submitting = true
    const body = { quest_id: quest.id, user_id: postedAsUserId }
    if (placeId) body.place_id = placeId
    if (postedAt) body.posted_at = postedAt
    if (isCuratorPost) body.curation_level = curationLevel

    if (isVideo) {
      uploadProgress = 0
      try {
        body.media_upload_id = await uploadVideoMedia()
      } catch (e) {
        failed(e.message)
        return
      }
    } else {
      if (mainImage) body.main_image_id = mainImage.id
      if (isDualPhoto && selfieImage) body.selfie_image_id = selfieImage.id
    }

    router.post(isCuratorPost ? '/quest-examples/curator_post' : '/quest-examples', body, {
      only: [...reload, 'flash'],
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        finish()
        open = false
        oncreated?.()
      },
      onError: (errors) =>
        failed(
          errors.example_code === 'ExpiredMediaUploadError'
            ? `The upload expired before it could be submitted — click ${submitLabel} to retry.`
            : errors.example || errors.base || 'The server rejected the request.'
        ),
      onNetworkError: () => failed('Network error — check your connection and retry.'),
      onCancel: finish
    })
  }
</script>

<!-- Closing mid-submit would orphan the prepared upload (and detach an in-flight
     video PUT), so the modal is locked while submitting. -->
<Modal bind:open title={isCuratorPost ? 'Create curator post' : 'Create Example submission'} dismissible={!submitting && !avatarModalOpen} {onclose}>
  <p class="text-sm text-base-content/60 mb-2">
    {#if isCuratorPost}
      Adds an approved, public curator post to the quest. Unrated by default; pick a rating to put it in the rated
      posts at the front of Quest Detail and the community feed's curated lane. One post per curator per quest — pick
      a poster who hasn't posted on this quest yet.
    {:else}
      Creates a reference post as the Zupp brand account or a quest curator, approved and public immediately, pinned
      first in the quest's Moments and timestamped to Posted at (defaults to the quest's start). A quest can have only
      one Example.
    {/if}
    {#if quest?.cycle_identifier}<span class="block mt-1 text-base-content">Quest: {quest.cycle_identifier}</span>{/if}
    {#if quest?.prompt}<span class="block text-base-content">{quest.prompt}</span>{/if}
  </p>

  {#if loading}
    <p class="text-sm text-base-content/60 py-3">Loading poster options…</p>
  {:else if loadFailed}
    <p class="text-sm text-error py-3">
      Couldn't load the poster options.
      <Button variant="link" size="sm" onclick={() => load(questId)}>Retry</Button>
    </p>
  {:else if quest}
    <fieldset class="fieldset">
      <legend class="fieldset-legend">Posted as</legend>
      {#if posterOptions.length === 0}
        <p class="text-error text-sm">
          No brand account or quest curators available — run <code>rake users:create_brand_account</code> on the
          server, or flag curator users on the Users page.
        </p>
      {:else}
        <div class="flex items-center gap-2">
          {#if selectedPoster?.icon_image_url}
            <img src={selectedPoster.icon_image_url} alt={selectedPoster.name || 'Poster'} class="h-8 w-8 rounded-full object-cover shrink-0" />
          {:else if selectedPoster}
            <span class="h-8 w-8 rounded-full bg-base-300 flex items-center justify-center text-sm font-semibold shrink-0">
              {(selectedPoster.name || '?').charAt(0).toUpperCase()}
            </span>
          {/if}
          <!-- Locked while submitting: the prepared upload is owned by this user. -->
          <Combobox
            bind:value={postedAsUserId}
            options={posterOptions}
            placeholder="Select who to post as"
            ariaLabel="Posted as"
            disabled={submitting}
          />
          {#if canEditAvatar && selectedPoster}
            <Button variant="link" size="xs" class="whitespace-nowrap" disabled={submitting} onclick={() => (avatarModalOpen = true)}>
              {selectedPoster.icon_image_url ? 'Change photo' : 'Set photo'}
            </Button>
          {/if}
        </div>
        {#if !posters.brand_account}
          <p class="label whitespace-normal">
            No brand account configured — pick a quest curator, or run rake users:create_brand_account on the server.
          </p>
        {/if}
      {/if}
    </fieldset>

    {#if placeOptions.length > 0}
      <fieldset class="fieldset">
        <legend class="fieldset-legend">Place (optional)</legend>
        <Combobox bind:value={placeId} options={placeOptions} clearable placeholder="No place" ariaLabel="Place" disabled={submitting} />
        <p class="label whitespace-normal">Must be one of the quest's eligible places.</p>
      </fieldset>
    {/if}

    <fieldset class="fieldset">
      <legend class="fieldset-legend">Posted at</legend>
      <div class="flex items-center gap-3">
        <input
          type="datetime-local"
          class="input w-full"
          aria-label="Posted at"
          value={postedAt || quest.active_starts_at_local || ''}
          max={quest.active_ends_at_local || undefined}
          disabled={submitting}
          oninput={(event) => (postedAt = event.target.value)}
        />
        {#if postedAt}
          <Button variant="link" size="sm" class="whitespace-nowrap" disabled={submitting} onclick={() => (postedAt = '')}>
            Reset to quest start
          </Button>
        {/if}
      </div>
      <p class="label whitespace-normal">
        The real date and time the post was taken; the app shows it as the post's timestamp. Defaults to the quest's
        start. Nothing shows before the quest is visible; a future time hides a curator post until then (an Example
        still shows on the quest page). Times are in the quest's College Town time zone ({quest.time_zone}).
        {#if quest.visible_starts_at_label}This quest becomes visible {quest.visible_starts_at_label}.{/if}
      </p>
    </fieldset>

    {#if isCuratorPost}
      <fieldset class="fieldset">
        <legend class="fieldset-legend">Rating (optional)</legend>
        <Combobox bind:value={curationLevel} options={curationLevels} ariaLabel="Rating" disabled={submitting} />
        <p class="label whitespace-normal">{ratingHelp}</p>
      </fieldset>
    {/if}

    {#if isVideo}
      <fieldset class="fieldset">
        <legend class="fieldset-legend">Video</legend>
        {#if videoFile && videoUrl}
          <div class="flex items-start gap-3">
            <!-- svelte-ignore a11y_media_has_caption -->
            <video src={videoUrl} controls muted playsinline class="w-44 max-h-60 rounded-box bg-neutral"></video>
            <span class="flex-1 text-sm break-all">{videoFile.name}</span>
            <Button variant="default" size="xs" disabled={submitting} onclick={removeVideo}>Remove</Button>
          </div>
        {:else}
          <label
            class="block p-4 text-center text-sm text-base-content/60 border-2 border-dashed rounded-box cursor-pointer {dragging ? 'border-primary bg-primary/10' : 'border-base-300'}"
            ondragover={(event) => {
              event.preventDefault()
              dragging = true
            }}
            ondragleave={() => (dragging = false)}
            ondrop={onVideoDropped}
          >
            {#if checkingVideo}
              Checking video…
            {:else}
              Drag an MP4 here, or click to browse (max 100 MB, {MAX_VIDEO_DURATION_LABEL_SECONDS} seconds)
            {/if}
            <input type="file" accept={VIDEO_MIME_TYPE} class="hidden" onchange={onVideoPicked} />
          </label>
        {/if}
      </fieldset>

      {#if videoFile}
        {#key videoFile}
          <VideoThumbnailPicker
            source={videoFile}
            disabled={submitting}
            onpick={({ blob }) => (thumbnailBlob = blob)}
            onerror={(e) => (error = { title: 'Could not read the video', text: e.message })}
          />
        {/key}
      {/if}
    {:else}
      <fieldset class="fieldset">
        <legend class="fieldset-legend">{isDualPhoto ? 'Back-camera image' : 'Image'}</legend>
        {#if mainImage}
          <div class="flex items-center gap-2">
            <ClickableImage url={mainImage.url} title="Main" thumbClass="h-20 w-20 object-contain bg-base-200 rounded-box" />
            <Button variant="default" size="xs" disabled={submitting} onclick={() => (mainImage = null)}>Remove</Button>
          </div>
        {:else}
          <div><Button variant="default" size="sm" onclick={() => (mainUploadOpen = true)}>Upload image</Button></div>
        {/if}
      </fieldset>

      {#if isDualPhoto}
        <fieldset class="fieldset">
          <legend class="fieldset-legend">Selfie (front-camera) image</legend>
          {#if selfieImage}
            <div class="flex items-center gap-2">
              <ClickableImage url={selfieImage.url} title="Selfie" thumbClass="h-20 w-20 object-contain bg-base-200 rounded-box" />
              <Button variant="default" size="xs" disabled={submitting} onclick={() => (selfieImage = null)}>Remove</Button>
            </div>
          {:else}
            <div><Button variant="default" size="sm" onclick={() => (selfieUploadOpen = true)}>Upload image</Button></div>
          {/if}
        </fieldset>
      {/if}
    {/if}

    {#if uploadProgress !== null}
      <div class="mt-4">
        <progress class="progress progress-primary w-full" value={Math.round(uploadProgress * 100)} max="100"></progress>
        <p class="text-xs text-base-content/60">Uploading video… {Math.round(uploadProgress * 100)}%</p>
      </div>
    {/if}
  {/if}

  {#if error}
    <div class="alert alert-error alert-soft mt-4 text-sm" role="alert">
      <div>
        <div class="font-semibold">{error.title}</div>
        {#if error.text}<div class="whitespace-pre-line">{error.text}</div>{/if}
      </div>
    </div>
  {/if}

  <div class="flex justify-end gap-2 mt-4">
    <Button variant="ghost" disabled={submitting} onclick={() => { open = false; onclose?.() }}>Cancel</Button>
    <Button variant="secondary" loading={submitting} disabled={!quest || !postedAsUserId || submitting} onclick={submit}>
      {submitLabel}
    </Button>
  </div>
</Modal>

<UserAvatarUploadModal bind:open={avatarModalOpen} user={selectedPoster} onsaved={() => reloadPrompt(questId)} />

<ImageUploadDrawer bind:open={mainUploadOpen} onuploaded={(image) => (mainImage = image)} />
<ImageUploadDrawer bind:open={selfieUploadOpen} onuploaded={(image) => (selfieImage = image)} />
